import { AxiosInstance, AxiosResponse } from 'axios';
import { EventsDto } from './dto/events.dto';
import { DEFAULT_EVENT_PARAMS } from '../constants/events';
import { RANGES } from '../constants/range';
import { RangeEnum } from '../enums/range';
import { isNil, map, filter as filterArr, toLower, toNumber, pickBy } from 'lodash-es';
import { EventCategory, EventResponseType, EventType } from '../types/event-types';
import { INTERESTS, InterestsCategoryKeysEnum } from '../constants';
import {
	EventToUserPeriodTypeEnum,
	EventToUserDto,
	EventInstanceFilter,
	ListWithTotals,
	EventParticipantsFilter,
	UserParticipantShortDtoWithIntakeFromData,
	InterestsEnum,
	CreateEventDto,
	UpdateEventDto,
	EventDto,
	EventInstanceSortEnum,
	EventSitemap,
	IntakeFormUserDataResults,
	InterestDto,
	EventCategoriesFilter,
	EventInstanceWithUserAttendanceDto,
	WaitlistedUserToEventDto,
	JoinWaitlistPublicDto,
	PublicEditEventTicketDto,
	PublicJoinEventBody,
	EventOptionsFilter,
	ShortEventInstanceDto,
} from '@sweatpals/shared';
import { EventsService as BaseEventsService } from '@sweatpals/data-services';
import { isValid } from 'date-fns';
import * as Sentry from '@sentry/nextjs';

type EventSearchParams = EventInstanceFilter & {
	paid?: string | boolean | null;
	range?: number;
	interests?: string[];
	periodFrom?: string | Date;
};

export class EventsService {
	private readonly httpClient: AxiosInstance;
	private readonly baseService: BaseEventsService;

	constructor(httpClient: AxiosInstance) {
		this.httpClient = httpClient;
		this.baseService = new BaseEventsService(httpClient);
	}

	public async getPublicEventsDates(params: EventSearchParams): Promise<Date[]> {
		const { data } = await this.baseService.findEventsDatesByFilterPublic(this._buildParams(params));

		return data;
	}

	public async getPublicEventsList(params: EventSearchParams): Promise<EventResponseType> {
		const { data } = await this.baseService.findByFilterPublic(this._buildParams(params));
		const { limit } = params;

		return EventsDto.buildDataFromResponse(data, limit);
	}

	public async getPublicExtendEventsList(params: EventSearchParams): Promise<EventResponseType> {
		const { data } = await this.baseService.findByFilterPublic(this._buildParams(params));
		const { limit } = params;

		return EventsDto.buildExtendDataFromResponse(data, limit);
	}

	public async getPublicEventOptionsByAlias(alias: string, params: EventOptionsFilter): Promise<ListWithTotals<ShortEventInstanceDto>> {
		const { data } = await this.httpClient.get<ListWithTotals<ShortEventInstanceDto>>(
			`/events/public/event-filter-options/alias/${alias}`,
			{ params }
		);

		return data;
	}

	public async getPublicNearestEventInstanceByAlias(
		eventAlias: string,
	): Promise<EventType | null> {
		const { data } = await this.httpClient.get<EventType>(
			`/events/public/nearest/instance/?alias=${eventAlias}`
		);

		return EventsDto.buildSingleEvent(data);
	}

	public async getEventList(params: EventSearchParams): Promise<EventResponseType> {
		const { data } = await this.baseService.findByFilter(this._buildParams(params));
		const { limit } = params;

		return EventsDto.buildExtendDataFromResponse(data, limit);
	}

	public async getEventListDirect(params: EventSearchParams): Promise<EventInstanceWithUserAttendanceDto[]> {
		const { data } = await this.baseService.findByFilter(this._buildParams(params));

		return data;
	}

	public async getPublicEventInstanceByAlias(
		eventAlias: string,
		shortLocalInstance?: string,
		source?: string
	): Promise<EventType | null> {
		// if no shortLocalInstance is provided, we need to fetch the nearest instance (first upcoming or latest past)
		if (!shortLocalInstance) {
			const nearest = await this.getPublicNearestEventInstanceByAlias(eventAlias);

			return nearest;
		}

		if (!isValid(new Date(shortLocalInstance))) {
			console.debug({ api: 'getPublicEventInstanceByAlias', eventAlias, shortLocalInstance, source });
			Sentry.captureException({
				message: 'Invalid time value',
				value: shortLocalInstance,
				source,
				alias: eventAlias,
			});

			return null;
		}

		const { data } = await this.baseService.getPublicEventInstanceByAlias(eventAlias, shortLocalInstance);

		return EventsDto.buildSingleEvent(data);
	}

	public async getPublicEventInstance(eventId: string, instance: string): Promise<EventType | null> {
		const { data } = await this.baseService.getPublicEventInstance(eventId as string, instance as string);

		return EventsDto.buildSingleEvent(data);
	}

	public async getEventInstance(eventId: string, instance: string): Promise<EventType | null> {
		const { data } = await this.baseService.getEventInstance(eventId as string, instance as string);

		return EventsDto.buildSingleEvent(data);
	}

	public async getPastEventOrInstance(eventId: string, instance: string | Date): Promise<EventType | null> {
		const { data } = await this.baseService.getPastEventOrInstance(eventId, instance);

		return EventsDto.buildSingleEvent(data);
	}

	public async getEventParticipants(
		eventId: string,
		filter: EventParticipantsFilter
	): Promise<ListWithTotals<EventToUserDto>> {
		const { data } = await this.baseService.getParticipants(eventId, filter);

		return data;
	}

	public async getEventParticipantEmails(
		eventId: string,
		filter: EventParticipantsFilter
	): Promise<UserParticipantShortDtoWithIntakeFromData[]> {
		const { data } = await this.baseService.getEventParticipantEmails(eventId, filter);

		return data;
	}

	public async joinEvent(
		eventId: string,
		instance: string | Date,
		type: EventToUserPeriodTypeEnum
	): Promise<EventToUserDto | null> {
		const { data } = await this.baseService.joinEvent(eventId as string, { instance: instance as any, type });

		return data;
	}

	public async publicJoinEvent(
		eventId: string,
		instance: string | Date,
		type: EventToUserPeriodTypeEnum,
		body: PublicJoinEventBody
	): Promise<EventToUserDto | null> {
		const { data } = await this.baseService.publicJoinEvent(eventId as string, { instance: instance as any, type }, body);

		return data;
	}

	public async joinEventWaitlist(eventId: string, instance: string): Promise<EventToUserDto | null> {
		const { data } = await this.baseService.joinEventWaitlist(eventId as string, instance);

		return data;
	}

	public async joinEventWaitlistV2(
		eventId: string,
		instance: string,
		waitlistUserData: JoinWaitlistPublicDto
	): Promise<WaitlistedUserToEventDto | null> {
		const { data } = await this.httpClient.post<WaitlistedUserToEventDto>(
			`events/public/${eventId}/join/waitlist/${instance}`,
			waitlistUserData
		);

		return data;
	}

	public async create(body: CreateEventDto): Promise<AxiosResponse<EventDto>> {
		return this.baseService.create(body);
	}

	public async update(eventId: string, body: UpdateEventDto): Promise<AxiosResponse<EventDto>> {
		return this.baseService.update(eventId, body);
	}

	public async findByMultipleFilters(filters: EventInstanceFilter[]): Promise<EventResponseType[]> {
		const response = await this.baseService.findByFilterPublicMultiple(filters);

		return map(response.data, (data) => EventsDto.buildDataFromResponse(data));
	}

	public async getEventInstanceShortLink(eventId: string, instanceDate: Date | string): Promise<string> {
		const { data } = await this.httpClient.get<string>(
			`/events/public/event/deeplink/${eventId}/instance/${
				instanceDate instanceof Date ? instanceDate.toISOString() : instanceDate
			}`
		);

		return data;
	}

	public async updateTicket(ticketId: string, body: PublicEditEventTicketDto): Promise<boolean> {
		const { data: isUpdated } = await this.httpClient.put<boolean>(`/events/public/update-ticket/${ticketId}`, body);

		return isUpdated;
	}

	public async getEventInstanceBranchLink(
		eventId: string,
		instanceDate: Date | string,
		fallbackToStore?: boolean
	): Promise<string> {
		const { data } = await this.httpClient.get<string>(
			`/events/event/branchlink/${eventId}/instance/${
				instanceDate instanceof Date ? instanceDate.toISOString() : instanceDate
			}`,
			{ params: { store: fallbackToStore } }
		);

		return data;
	}

	public async getHomeCategories(filter?: { cityId?: string }): Promise<EventCategory[]> {
		const filters: EventInstanceFilter[] = map(INTERESTS, (category) => ({
			...this._buildParams({
				...DEFAULT_EVENT_PARAMS,
				fitnessTypes: category.value,
				range: RangeEnum.All,
				interests: [category.name],
				limit: 15,
				...filter,
			}),
		}));

		const { data: response } = await this.baseService.findByFilterPublicMultiple(filters);

		const result: EventCategory[] = [];

		for (let i = 0; i < response.length; i++) {
			const singleResponse = response[i];
			const filter = filters[i];

			if (singleResponse[0]?.fitnessTypeId) {
				const initialData = singleResponse[0];

				const interest = {
					id: initialData?.fitnessTypeId,
					name: this._setFitnessTypeName(initialData?.fitnessType?.name),
					data: EventsDto.buildDataFromResponse(singleResponse, 15),
					renderName:
						INTERESTS[initialData?.fitnessTypeId]?.renderName ||
						this._setFitnessTypeName(initialData?.fitnessType?.name),
					hasPriority: initialData?.fitnessType?.hasPriorityOverNativeAsset ?? null,
					filter,
				};

				result.push(interest);
			}
		}

		return filterArr(result, (instance) => !isNil(instance));
	}

	public async getNewHomeCategories(filter?: EventSearchParams): Promise<EventCategory[]> {
		const categories: EventCategory[] = [
			{
				filter: {
					...DEFAULT_EVENT_PARAMS,
					...this._buildParams(filter),
					isEditorPick: true,
					oneInstancePerEvent: true,
				},
				id: 'editor-picks',
				renderName: 'Editor Picks',
			},
			{
				filter: {
					...DEFAULT_EVENT_PARAMS,
					...filter,
					sort: EventInstanceSortEnum.CreatedAtDesc,
					oneInstancePerEvent: true,
				},
				id: 'just-added',
				renderName: 'Just Added',
			},
			{
				filter: {
					...DEFAULT_EVENT_PARAMS,
					...this._buildParams(filter),
					fitnessTypes: INTERESTS[InterestsCategoryKeysEnum.RunWalk]?.value,
					oneInstancePerEvent: true,
				},
				id: 'run-walk',
				renderName: 'Run & Walk',
			},
			{
				filter: {
					...DEFAULT_EVENT_PARAMS,
					...this._buildParams(filter),
					fitnessTypes: INTERESTS[InterestsCategoryKeysEnum.Dance]?.value,
					oneInstancePerEvent: true,
				},
				id: 'dance',
				renderName: 'Dance',
			},
			{
				filter: {
					...DEFAULT_EVENT_PARAMS,
					...this._buildParams(filter),
					fitnessTypes: INTERESTS[InterestsCategoryKeysEnum.Mindfulness]?.value,
					oneInstancePerEvent: true,
				},
				id: 'mindfullness',
				renderName: 'Mindfullness',
			},
			{
				filter: {
					...DEFAULT_EVENT_PARAMS,
					...this._buildParams(filter),
					online: true,
					oneInstancePerEvent: true,
				},
				id: 'online',
				renderName: 'Online',
			},
		];

		const filters = categories.map((category) => category.filter);

		const { data: response } = await this.baseService.findByFilterPublicMultiple(filters);

		for (let i = 0; i < categories.length; i++) {
			const data = response[i];
			const category = categories[i];

			category.data = EventsDto.buildDataFromResponse(data, filter?.limit);
		}

		return categories;
	}

	public async getEventSitemap(filter: EventInstanceFilter): Promise<EventSitemap[]> {
		const { data } = await this.baseService.getEventsSitemap(filter);

		return data;
	}

	public async getEventsCategories(filter: EventCategoriesFilter): Promise<InterestDto[]> {
		const { data } = await this.baseService.getEventsCategories(filter);

		return data;
	}

	public async checkIntakeSigned(eventId: string, instance: string): Promise<boolean> {
		const { data } = await this.baseService.checkIntakePassed(eventId, instance);

		return data;
	}

	public async singIntakeForm(eventId: string, instance: string, body: IntakeFormUserDataResults): Promise<boolean> {
		const { data } = await this.baseService.singIntakeForm(eventId, instance, body);

		return data;
	}

	public async getEventsCountPublic(filter: EventInstanceFilter): Promise<number> {
		const { data } = await this.baseService.getEventsCountPublic(filter);

		return data;
	}

	public async getEventGroupChatInvitationCode(eventId: string): Promise<string> {
		const { data } = await this.baseService.getEventGroupChatInvitationCode(eventId);

		return data;
	}

	private _setFitnessTypeName(name?: string): string {
		const interest = Object.values(INTERESTS).find((interest) => interest.name.toLowerCase() === name?.toLowerCase());

		if (interest) {
			return interest.name;
		}

		return INTERESTS[InterestsEnum.Others].name;
	}

	private _buildParams(params?: EventSearchParams): EventInstanceFilter {
		const {
			range,
			periodFrom,
			periodTo,
			fitnessTypes: fitnessTypesArray,
			interests: interestString,
			paid,
			limit,
			...rest
		} = params ?? {};

		let from = periodFrom;
		let to = periodTo;
		let fitnessTypes = fitnessTypesArray;
		let newLimit;

		const interests = typeof interestString === 'string' ? (interestString as string).split(',') : interestString;

		if (interests?.length) {
			const foundInterests = filterArr(INTERESTS, (interest) =>
				interests.map(toLower).includes(toLower(interest?.name))
			);

			if (foundInterests?.length) {
				fitnessTypes = foundInterests.map((interest) => interest.value).flat(2);
			}
		}

		if (toNumber(range) === toNumber(RangeEnum.Custom)) {
			from = periodFrom;
			to = periodTo;
		} else if (range) {
			const { startDate, endDate } = RANGES[range]();
			from = periodFrom || startDate;
			to = periodTo || endDate;
		}

		if (limit) {
			newLimit = limit + 1;
		}

		return pickBy(
			{
				...DEFAULT_EVENT_PARAMS,
				...rest,
				limit: newLimit,
				paid: typeof paid === 'string' ? paid === 'true' : paid,
				periodFrom: from,
				periodTo: to,
				fitnessTypes,
			},
			(value) => !(value === undefined || value === null || value === '')
		);
	}
}
